import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import PropTypes from "prop-types";
import FormInput from "./FormInput";
import SubmitButton from "./SubmitButton";
import { submit_gd_FormData, submit_vip_FormData } from "../api/vipform";
import { toast } from "react-toastify";
import axios from "axios";

const BASE_URL = process.env.REACT_APP_BASE_URL;

/* ---------------- MODERN STEP HEADER WITH PROGRESS BAR ---------------- */
const StepHeader = ({ step }) => (
  <div style={styles.stepsContainer}>
    <div style={styles.progressBar}>
      <div style={{ ...styles.progressFill, width: `${((step - 1) / 2) * 100}%` }} />
    </div>
    <div style={styles.steps}>
      {[1, 2, 3].map((s) => (
        <div key={s} style={styles.stepWrapper}>
          <div
            style={{
              ...styles.stepCircle,
              background: step >= s ? "#2563eb" : "rgba(255,255,255,0.15)",
              border: step >= s ? "3px solid #60a5fa" : "2px solid rgba(255,255,255,0.3)",
              boxShadow: step === s ? "0 0 15px rgba(37, 99, 235, 0.6)" : "none",
            }}
          >
            {step > s ? "✓" : s}
          </div>
          <span style={styles.stepText}>Step {s}</span>
        </div>
      ))}
    </div>
  </div>
);

StepHeader.propTypes = {
  step: PropTypes.number.isRequired,
};

export default function Registration() {
  const [activeType, setActiveType] = useState("guard");
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);

  const [guardStep, setGuardStep] = useState(1);
  const [vipStep, setVipStep] = useState(1);

  /* ---------------- GUARD STATE ---------------- */
  const [guardForm, setGuardForm] = useState({
    name: "",
    email: "",
    gender: "",
    pnNumber: "",
    adharNo: "",
    username: "",
    password: "",
    rank: "",
    experience: "",
    contactno: "",
    status: "self",
  });

  const [ranks, setRanks] = useState([]);
  const [otherRank, setOtherRank] = useState("");
  const [emailError, setEmailError] = useState("");
  const [contactError, setContactError] = useState("");
  const [imageFile, setImageFile] = useState(null);

  /* ---------------- VIP STATE ---------------- */
  const [vipForm, setVipForm] = useState({
    name: "",
    email: "",
    gender: "",
    adharNo: "",
    username: "",
    password: "",
    designation: "",
    contactno: "",
    status: "self",
  });

  const [designations, setDesignations] = useState([]);
  const [otherDesignation, setOtherDesignation] = useState("");
  const [vipImageFile, setVipImageFile] = useState(null);

  /* ---------------- FETCH DROPDOWNS ---------------- */
  useEffect(() => {
    const fetchData = async () => {
      try {
        const r = await axios.get(`${BASE_URL}/api/officer/unique-ranks`);
        setRanks(Array.isArray(r.data) ? r.data : []);
      } catch {
        setRanks([]);
      }

      try {
        const d = await axios.get(`${BASE_URL}/api/categories/unique-designation`);
        setDesignations(Array.isArray(d.data) ? d.data : []);
      } catch {
        setDesignations([]);
      }
    };

    fetchData();
  }, []);

  /* ---------------- HANDLERS ---------------- */
  const handleGuardChange = (e) => {
    const { name, value } = e.target;

    if (name === "email") {
      setGuardForm((p) => ({ ...p, email: value }));
      const r = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      setEmailError(r.test(value) ? "" : "Invalid email format");
      return;
    }

    if (name === "contactno") {
      if (/^\d{0,10}$/.test(value)) {
        setGuardForm((p) => ({ ...p, contactno: value }));
        setContactError(value.length === 10 ? "" : "Contact must be 10 digits");
      }
      return;
    }

    if (name === "adharNo") {
      if (/^\d{0,12}$/.test(value)) {
        setGuardForm((p) => ({ ...p, adharNo: value }));
      }
      return;
    }

    setGuardForm((p) => ({ ...p, [name]: value }));
  };

  const handleVipChange = (e) => {
    const { name, value } = e.target;

    if (name === "email") {
      setVipForm((p) => ({ ...p, email: value }));
      const r = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      setEmailError(r.test(value) ? "" : "Invalid email format");
      return;
    }

    if (name === "contactno") {
      if (/^\d{0,10}$/.test(value)) {
        setVipForm((p) => ({ ...p, contactno: value }));
        setContactError(value.length === 10 ? "" : "Contact must be 10 digits");
      }
      return;
    }

    if (name === "adharNo") {
      if (/^\d{0,12}$/.test(value)) {
        setVipForm((p) => ({ ...p, adharNo: value }));
      }
      return;
    }
    setVipForm((p) => ({ ...p, [name]: value }));
  };

  /* ---------------- IMAGE SELECT ---------------- */
  const handleImageSelect = (e) => {
    setImageFile(e.target.files[0]);
  };

  const handleVipImageSelect = (e) => {
    setVipImageFile(e.target.files[0]);
  };

  /* ---------------- IMAGE UPLOAD ---------------- */
  const uploadImage = async (guardId) => {
    if (!imageFile) return;

    const formData = new FormData();
    formData.append("imaged", imageFile);

    await axios.post(`${BASE_URL}/api/profile/upload/${guardId}/guard`, formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });
  };

  const uploadVipImage = async (vipId) => {
    if (!vipImageFile) return;

    const formData = new FormData();
    formData.append("imaged", vipImageFile);

    await axios.post(`${BASE_URL}/api/profile/upload/${vipId}/vip`, formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });
  };

  /* ---------------- SUBMIT HANDLERS ---------------- */
  const handleGuardSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    if (emailError || contactError) {
      toast.error("Please fix all errors!");
      setLoading(false);
      return;
    }

    if (!imageFile) {
      toast.error("Please select profile picture!");
      setLoading(false);
      return;
    }

    try {
      const res = await submit_gd_FormData({
        ...guardForm,
        rank: guardForm.rank === "Other" ? otherRank : guardForm.rank,
      });

      const guardId = res?.data?.id || res?.id;

      if (!guardId) {
        toast.error("Guard ID not received");
        return;
      }

      await uploadImage(guardId);

      toast.success("Officer Registered Successfully");
      resetGuard();
      navigate("/login");
    } catch (error) {
      toast.error("Officer Registration Failed");
    } finally {
      setLoading(false);
    }
  };

  const handleVipSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    if (emailError || contactError) {
      toast.error("Please fix all errors!");
      setLoading(false);
      return;
    }

    if (!vipImageFile) {
      toast.error("Please select VIP profile picture!");
      setLoading(false);
      return;
    }

    try {
      const res = await submit_vip_FormData({
        ...vipForm,
        designation: vipForm.designation === "Other" ? otherDesignation : vipForm.designation,
      });

      const vipId = res?.data?.id || res?.id;

      if (!vipId) {
        toast.error("VIP ID not received");
        return;
      }

      await uploadVipImage(vipId);

      toast.success("VIP Registered Successfully");
      resetVip();
      navigate("/login");
    } catch (error) {
      toast.error("VIP Registration Failed");
    } finally {
      setLoading(false);
    }
  };

  const resetGuard = () => {
    setGuardStep(1);
    setGuardForm({
      name: "", email: "", gender: "", pnNumber: "", adharNo: "", username: "", password: "",
      rank: "", experience: "", contactno: "", status: "self",
    });
    setOtherRank("");
    setImageFile(null);
  };

  const resetVip = () => {
    setVipStep(1);
    setVipForm({
      name: "", email: "", gender: "", adharNo: "", username: "", password: "",
      designation: "", contactno: "", status: "self",
    });
    setOtherDesignation("");
    setVipImageFile(null);
  };

  /* ---------------- RENDER ---------------- */
  return (
    <div style={styles.page}>
      <div style={styles.overlay} />

      <div style={styles.switchBox}>
        <button className="btn btn-outline-light btn-sm" onClick={() => navigate("/login")}>
          ← Back to Login
        </button>
        <div style={styles.toggleGroup}>
          <button onClick={() => setActiveType("guard")} style={styles.toggleBtn(activeType === "guard")}>
            Officer
          </button>
          <button onClick={() => setActiveType("vip")} style={styles.toggleBtn(activeType === "vip")}>
            VIP
          </button>
        </div>
      </div>

      <div style={styles.card}>
        {activeType === "guard" ? (
          <>
            <StepHeader step={guardStep} />
            <div className="text-center mb-5">
              <h1 style={styles.title}>Officer Registration Form</h1>
              <p style={styles.subtitle}>Please fill in the details carefully</p>
            </div>

            <form onSubmit={handleGuardSubmit}>
              {guardStep === 1 && (
                <>
                  <FormInput label="Name" name="name" required value={guardForm.name} onChange={handleGuardChange} />
                  <FormInput label="Email" name="email" required value={guardForm.email} onChange={handleGuardChange} />
                  {emailError && <p style={styles.error}>{emailError}</p>}
                  <label style={styles.label}>Gender *</label>
                  <select name="gender" required value={guardForm.gender} onChange={handleGuardChange} style={styles.select}>
                    <option value="">Select Gender</option>
                    <option>Male</option>
                    <option>Female</option>
                    <option>Other</option>
                  </select>
                  <FormInput label="Phone" name="contactno" required value={guardForm.contactno} onChange={handleGuardChange} />
                  {contactError && <p style={styles.error}>{contactError}</p>}
                </>
              )}

              {guardStep === 2 && (
                <>
                  <FormInput label="PN Number" name="pnNumber" required value={guardForm.pnNumber} onChange={handleGuardChange} />
                  <FormInput label="Aadhaar Number" name="adharNo" required value={guardForm.adharNo} onChange={handleGuardChange} />
                  <FormInput label="Work Experience (Years)" name="experience" required value={guardForm.experience} onChange={handleGuardChange} />
                  <label style={styles.label}>Rank *</label>
                  <select name="rank" required value={guardForm.rank} onChange={handleGuardChange} style={styles.select}>
                    <option value="">Select Rank</option>
                    {ranks.map((r, i) => <option key={i}>{r}</option>)}
                    <option value="Other">Other</option>
                  </select>
                  {guardForm.rank === "Other" && (
                    <FormInput label="Specify Rank" value={otherRank} onChange={(e) => setOtherRank(e.target.value)} required />
                  )}
                </>
              )}

              {guardStep === 3 && (
                <>
                  <label style={styles.label}>Profile Picture *</label>
                  <input type="file" accept="image/*" onChange={handleImageSelect} required style={styles.fileInput} />
                  <FormInput label="Username" name="username" required value={guardForm.username} onChange={handleGuardChange} />
                  <FormInput label="Password" name="password" type="password" required value={guardForm.password} onChange={handleGuardChange} />
                </>
              )}

              <div style={styles.navBtns}>
                {guardStep > 1 && (
                  <button type="button" style={styles.backBtn} onClick={() => setGuardStep(guardStep - 1)}>
                    Back
                  </button>
                )}
                {guardStep < 3 ? (
                  <button type="button" style={styles.nextBtn} onClick={() => setGuardStep(guardStep + 1)}>
                    Next →
                  </button>
                ) : (
                  <SubmitButton label={loading ? "Submitting..." : "Register Officer"} disabled={loading} />
                )}
              </div>
            </form>
          </>
        ) : (
          <>
            <StepHeader step={vipStep} />
            <div className="text-center mb-5">
              <h1 style={styles.title}>VIP Registration Form</h1>
              <p style={styles.subtitle}>Please fill in the details carefully</p>
            </div>

            <form onSubmit={handleVipSubmit}>
              {vipStep === 1 && (
                <>
                  <FormInput label="Name" name="name" required value={vipForm.name} onChange={handleVipChange} />
                  <FormInput label="Email" name="email" required value={vipForm.email} onChange={handleVipChange} />
                  {emailError && <p style={styles.error}>{emailError}</p>}
                  <FormInput label="Phone" name="contactno" required value={vipForm.contactno} onChange={handleVipChange} />
                  {contactError && <p style={styles.error}>{contactError}</p>}
                </>
              )}

              {vipStep === 2 && (
                <>
                  <label style={styles.label}>Gender *</label>
                  <select name="gender" required value={vipForm.gender} onChange={handleVipChange} style={styles.select}>
                    <option value="">Select Gender</option>
                    <option>Male</option>
                    <option>Female</option>
                    <option>Other</option>
                  </select>
                  <FormInput label="Aadhaar Number" name="adharNo" required value={vipForm.adharNo} onChange={handleVipChange} />
                  <label style={styles.label}>Profile Picture *</label>
                  <input type="file" accept="image/*" onChange={handleVipImageSelect} required style={styles.fileInput} />
                </>
              )}

              {vipStep === 3 && (
                <>
                  <label style={styles.label}>Designation *</label>
                  <select name="designation" required value={vipForm.designation} onChange={handleVipChange} style={styles.select}>
                    <option value="">Select</option>
                    {designations.map((d, i) => (
                      <option key={i} value={d.name || d}>
                        {d.name || d}
                      </option>
                    ))}
                    <option value="Other">Other</option>
                  </select>
                  {vipForm.designation === "Other" && (
                    <FormInput label="Other Designation" required value={otherDesignation} onChange={(e) => setOtherDesignation(e.target.value)} />
                  )}
                  <FormInput label="Username" name="username" required value={vipForm.username} onChange={handleVipChange} />
                  <FormInput label="Password" name="password" type="password" required value={vipForm.password} onChange={handleVipChange} />
                </>
              )}

              <div style={styles.navBtns}>
                {vipStep > 1 && (
                  <button type="button" style={styles.backBtn} onClick={() => setVipStep(vipStep - 1)}>
                    Back
                  </button>
                )}
                {vipStep < 3 ? (
                  <button type="button" style={styles.nextBtn} onClick={() => setVipStep(vipStep + 1)}>
                    Next →
                  </button>
                ) : (
                  <SubmitButton label={loading ? "Submitting..." : "Register VIP"} disabled={loading} />
                )}
              </div>
            </form>
          </>
        )}
      </div>
    </div>
  );
}

/* ---------------- ENHANCED STYLES (WIDER & SHORTER FORM) ---------------- */
const styles = {
  page: {
    minHeight: "100vh",
    backgroundImage: "url('/assets/img/loginPage.png')",
    backgroundSize: "cover",
    backgroundPosition: "center",
    position: "relative",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    padding: "30px 20px",
  },
  overlay: {
    position: "absolute",
    top: 0, left: 0, right: 0, bottom: 0,
    background: "rgba(0, 0, 0, 0.4)",
    pointerEvents: "none",
  },
  switchBox: {
    position: "relative",
    zIndex: 10,
    display: "flex",
    gap: 20,
    marginBottom: 30,
    alignItems: "center",
    flexWrap: "wrap",
    justifyContent: "center",
  },
  toggleGroup: {
    display: "flex",
    background: "rgba(255,255,255,0.1)",
    borderRadius: 16,
    overflow: "hidden",
    border: "1px solid rgba(255,255,255,0.2)",
    backdropFilter: "blur(8px)",
  },
  toggleBtn: (active) => ({
    padding: "14px 32px",
    background: active ? "linear-gradient(135deg, #2563eb, #3b82f6)" : "transparent",
    color: "#fff",
    fontWeight: 600,
    fontSize: "16px",
    cursor: "pointer",
    transition: "all 0.3s ease",
    border: "none",
    minWidth: "120px",
  }),
  card: {
    position: "relative",
    zIndex: 10,
    width: "100%",
    maxWidth: 720,                          // Wider form
    background: "rgba(15, 23, 42, 0.65)",
    backdropFilter: "blur(16px)",
    WebkitBackdropFilter: "blur(16px)",
    borderRadius: "24px",
    border: "1px solid rgba(255, 255, 255, 0.15)",
    padding: "36px 48px",                   // Less vertical padding → shorter height
    boxShadow: "0 20px 50px rgba(0, 0, 0, 0.6), 0 0 30px rgba(37, 99, 235, 0.2)",
  },
  stepsContainer: { marginBottom: 36, textAlign: "center" },
  progressBar: { height: 4, background: "rgba(255,255,255,0.15)", borderRadius: 2, overflow: "hidden", marginBottom: 20 },
  progressFill: { height: "100%", background: "linear-gradient(90deg, #2563eb, #60a5fa)", borderRadius: 2, transition: "width 0.4s ease" },
  steps: { display: "flex", justifyContent: "space-between" },
  stepWrapper: { flex: 1, textAlign: "center" },
  stepCircle: {
    width: 50,
    height: 50,
    borderRadius: "50%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    margin: "0 auto 12px",
    fontWeight: 700,
    fontSize: "18px",
    color: "#fff",
    transition: "all 0.3s ease",
  },
  stepText: { fontSize: "14px", color: "#e2e8f0", fontWeight: 500 },
  title: { fontSize: "34px", fontWeight: 800, color: "#ffffff", marginBottom: "10px", textShadow: "0 2px 10px rgba(0,0,0,0.3)" },
  subtitle: { color: "#cbd5e1", fontSize: "16px", fontWeight: 400 },
  label: { display: "block", marginBottom: 8, fontWeight: 700, color: "#e2e8f0", fontSize: "15px" },
  select: {
    width: "97%",
    marginLeft:"10px",
    padding: "14px",
    borderRadius: "12px",
    border: "1px solid rgba(255,255,255,0.2)",
    background: "",
    color: "",
    fontSize: "16px",
    marginBottom: "21px",
    transition: "all 0.3s ease",
  },
  fileInput: {
    width: "100%",
    padding: "18px",
    borderRadius: "14px",
    border: "2px dashed rgba(59, 130, 246, 0.6)",
    background: "rgba(59, 130, 246, 0.08)",
    color: "#e2e8f0",
    marginBottom: "22px",
    cursor: "pointer",
    transition: "all 0.3s ease",
  },
  error: { color: "#fca5a5", fontSize: "14px", margin: "-8px 0 16px", fontWeight: 500 },
  navBtns: { display: "flex", justifyContent: "space-between", marginTop: "36px" },
  backBtn: {
    padding: "14px 32px",
    borderRadius: "14px",
    background: "rgba(255,255,255,0.1)",
    border: "1px solid rgba(255,255,255,0.3)",
    color: "#e2e8f0",
    fontWeight: 600,
    cursor: "pointer",
    transition: "all 0.3s ease",
  },
  nextBtn: {
    padding: "14px 40px",
    borderRadius: "14px",
    background: "linear-gradient(135deg, #2563eb, #3b82f6)",
    border: "none",
    color: "#fff",
    fontWeight: 700,
    fontSize: "16px",
    cursor: "pointer",
    boxShadow: "0 8px 20px rgba(37, 99, 235, 0.4)",
    transition: "all 0.3s ease",
  },
};



// import React, { useEffect, useState } from "react";
// import { useNavigate } from "react-router-dom";
// import PropTypes from "prop-types";
// import FormInput from "./FormInput";
// import SubmitButton from "./SubmitButton";
// import { submit_gd_FormData, submit_vip_FormData } from "../api/vipform";
// import { toast } from "react-toastify";
// import axios from "axios";

// const BASE_URL = process.env.REACT_APP_BASE_URL;

// /* ---------------- STEP HEADER ---------------- */
// const StepHeader = ({ step }) => (
//   <div style={styles.steps}>
//     {[1, 2, 3].map((s) => (
//       <div key={s} style={styles.stepWrapper}>
//         <div
//           style={{
//             ...styles.stepCircle,
//             background: step >= s ? "#3B82F6" : "rgba(255,255,255,0.15)",
//             border: step >= s ? "none" : "1px solid rgba(255,255,255,0.3)",
//           }}
//         >
//           {s}
//         </div>
//         <span style={styles.stepText}>Step {s}</span>
//       </div>
//     ))}
//   </div>
// );

// StepHeader.propTypes = {
//   step: PropTypes.number.isRequired,
// };

// export default function Registration() {
//   const [activeType, setActiveType] = useState("guard");
//   const navigate = useNavigate();
//   const [loading, setLoading] = useState(false);

//   const [guardStep, setGuardStep] = useState(1);
//   const [vipStep, setVipStep] = useState(1);

//   /* ---------------- GUARD STATE ---------------- */
//   const [guardForm, setGuardForm] = useState({
//     name: "",
//     email: "",
//     gender: "",
//     pnNumber: "",
//     adharNo: "",
//     username: "",
//     password: "",
//     rank: "",
//     experience: "",
//     contactno: "",
//     status: "self",
//   });

//   const [ranks, setRanks] = useState([]);
//   const [otherRank, setOtherRank] = useState("");
//   const [emailError, setEmailError] = useState("");
//   const [contactError, setContactError] = useState("");
//   const [imageFile, setImageFile] = useState(null);

//   /* ---------------- VIP STATE ---------------- */
//   const [vipForm, setVipForm] = useState({
//     name: "",
//     email: "",
//     gender: "",
//     adharNo: "",
//     username: "",
//     password: "",
//     designation: "",
//     contactno: "",
//     status: "self",
//   });

//   const [designations, setDesignations] = useState([]);
//   const [otherDesignation, setOtherDesignation] = useState("");
//   const [vipImageFile, setVipImageFile] = useState(null);

//   /* ---------------- FETCH DROPDOWNS ---------------- */
//   useEffect(() => {
//     const fetchData = async () => {
//       try {
//         const r = await axios.get(`${BASE_URL}/api/officer/unique-ranks`);
//         setRanks(Array.isArray(r.data) ? r.data : []);
//       } catch {
//         setRanks([]);
//       }

//       try {
//         const d = await axios.get(`${BASE_URL}/api/categories/unique-designation`);
//         setDesignations(Array.isArray(d.data) ? d.data : []);
//       } catch {
//         setDesignations([]);
//       }
//     };

//     fetchData();
//   }, []);

//   /* ---------------- HANDLERS ---------------- */
//   const handleGuardChange = (e) => {
//     const { name, value } = e.target;

//     if (name === "email") {
//       setGuardForm((p) => ({ ...p, email: value }));
//       const r = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
//       setEmailError(r.test(value) ? "" : "Invalid email format");
//       return;
//     }

//     if (name === "contactno") {
//       if (/^\d{0,10}$/.test(value)) {
//         setGuardForm((p) => ({ ...p, contactno: value }));
//         setContactError(value.length === 10 ? "" : "Contact must be 10 digits");
//       }
//       return;
//     }

//     if (name === "adharNo") {
//       if (/^\d{0,12}$/.test(value)) {
//         setGuardForm((p) => ({ ...p, adharNo: value }));
//       }
//       return;
//     }

//     setGuardForm((p) => ({ ...p, [name]: value }));
//   };

//   const handleVipChange = (e) => {
//     const { name, value } = e.target;

//     if (name === "email") {
//       setVipForm((p) => ({ ...p, email: value }));
//       const r = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
//       setEmailError(r.test(value) ? "" : "Invalid email format");
//       return;
//     }

//     if (name === "contactno") {
//       if (/^\d{0,10}$/.test(value)) {
//         setVipForm((p) => ({ ...p, contactno: value }));
//         setContactError(value.length === 10 ? "" : "Contact must be 10 digits");
//       }
//       return;
//     }

//     if (name === "adharNo") {
//       if (/^\d{0,12}$/.test(value)) {
//         setVipForm((p) => ({ ...p, adharNo: value }));
//       }
//       return;
//     }
//     setVipForm((p) => ({ ...p, [name]: value }));
//   };

//   /* ---------------- IMAGE SELECT ---------------- */
//   const handleImageSelect = (e) => {
//     setImageFile(e.target.files[0]);
//   };

//   const handleVipImageSelect = (e) => {
//     setVipImageFile(e.target.files[0]);
//   };

//   /* ---------------- IMAGE UPLOAD ---------------- */
//   const uploadImage = async (guardId) => {
//     if (!imageFile) return;

//     const formData = new FormData();
//     formData.append("imaged", imageFile);

//     await axios.post(`${BASE_URL}/api/profile/upload/${guardId}/guard`, formData, {
//       headers: { "Content-Type": "multipart/form-data" },
//     });
//   };

//   const uploadVipImage = async (vipId) => {
//     if (!vipImageFile) return;

//     const formData = new FormData();
//     formData.append("imaged", vipImageFile);

//     await axios.post(`${BASE_URL}/api/profile/upload/${vipId}/vip`, formData, {
//       headers: { "Content-Type": "multipart/form-data" },
//     });
//   };

//   /* ---------------- SUBMIT ---------------- */
//   const handleGuardSubmit = async (e) => {
//     e.preventDefault();
//     setLoading(true);

//     if (emailError || contactError) {
//       toast.error("Please fix all errors!");
//       setLoading(false);
//       return;
//     }

//     if (!imageFile) {
//       toast.error("Please select profile picture!");
//       setLoading(false);
//       return;
//     }

//     try {
//       const res = await submit_gd_FormData({
//         ...guardForm,
//         rank: guardForm.rank === "Other" ? otherRank : guardForm.rank,
//       });

//       const guardId = res?.data?.id || res?.id;

//       if (!guardId) {
//         toast.error("Guard ID not received");
//         return;
//       }

//       await uploadImage(guardId);

//       toast.success("Officer Registered Successfully");
//       resetGuard();
//       navigate("/login");
//     } catch (error) {
//       toast.error("Officer Registration Failed");
//     } finally {
//       setLoading(false);
//     }
//   };

//   const handleVipSubmit = async (e) => {
//     e.preventDefault();
//     setLoading(true);

//     if (emailError || contactError) {
//       toast.error("Please fix all errors!");
//       setLoading(false);
//       return;
//     }

//     if (!vipImageFile) {
//       toast.error("Please select VIP profile picture!");
//       setLoading(false);
//       return;
//     }

//     try {
//       const res = await submit_vip_FormData({
//         ...vipForm,
//         designation: vipForm.designation === "Other" ? otherDesignation : vipForm.designation,
//       });

//       const vipId = res?.data?.id || res?.id;

//       if (!vipId) {
//         toast.error("VIP ID not received");
//         return;
//       }

//       await uploadVipImage(vipId);

//       toast.success("VIP Registered Successfully");
//       resetVip();
//       navigate("/login");
//     } catch (error) {
//       toast.error("VIP Registration Failed");
//     } finally {
//       setLoading(false);
//     }
//   };

//   const resetGuard = () => {
//     setGuardStep(1);
//     setGuardForm({
//       name: "",
//       email: "",
//       gender: "",
//       pnNumber: "",
//       adharNo: "",
//       username: "",
//       password: "",
//       rank: "",
//       experience: "",
//       contactno: "",
//       status: "self",
//     });
//     setOtherRank("");
//     setImageFile(null);
//   };

//   const resetVip = () => {
//     setVipStep(1);
//     setVipForm({
//       name: "",
//       email: "",
//       gender: "",
//       adharNo: "",
//       username: "",
//       password: "",
//       designation: "",
//       contactno: "",
//       status: "self",
//     });
//     setOtherDesignation("");
//     setVipImageFile(null);
//   };

//   /* ---------------- UI ---------------- */
//   return (
//     <div style={styles.page}>
//       <div style={styles.switchBox}>
//         <button className="btn btn-success" type="button" onClick={() => navigate("/login")}>
//           Back to login page
//         </button>
//         <button
//           onClick={() => setActiveType("guard")}
//           style={styles.switchBtn(activeType === "guard")}
//         >
//           Officer
//         </button>
//         <button
//           onClick={() => setActiveType("vip")}
//           style={styles.switchBtn(activeType === "vip")}
//         >
//           VIP
//         </button>
//       </div>

//       <div style={styles.card}>
//         {activeType === "guard" ? (
//           <>
//             <StepHeader step={guardStep} />
//             <form onSubmit={handleGuardSubmit}>
//               <div className="text-center mb-4">
//                 <h1 className="fw-bold display-6" style={{ color: "#ffffff" }}>
//                   Officer Registration Form
//                 </h1>
//                 <p style={{ color: "#e0e0e0" }}>Please fill in the details carefully</p>
//               </div>

//               {guardStep === 1 && (
//                 <>
//                   <FormInput label="Name" name="name" required value={guardForm.name} onChange={handleGuardChange} />
//                   <FormInput label="Email" name="email" required value={guardForm.email} onChange={handleGuardChange} />
//                   {emailError && <p style={styles.errorText}>{emailError}</p>}
//                   <label style={styles.label}>Gender *</label>
//                   <select name="gender" required value={guardForm.gender} onChange={handleGuardChange} style={styles.select}>
//                     <option value="">Select Gender</option>
//                     <option>Male</option>
//                     <option>Female</option>
//                     <option>Other</option>
//                   </select>
//                   <FormInput label="Phone" name="contactno" required value={guardForm.contactno} onChange={handleGuardChange} />
//                   {contactError && <p style={styles.errorText}>{contactError}</p>}
//                 </>
//               )}

//               {guardStep === 2 && (
//                 <>
//                   <FormInput label="PN Number" name="pnNumber" required value={guardForm.pnNumber} onChange={handleGuardChange} />
//                   <FormInput label="Aadhaar Number" name="adharNo" required value={guardForm.adharNo} onChange={handleGuardChange} />
//                   <FormInput label="Work Experience* (Enter Only the No. of Years)" name="experience" type="text" required value={guardForm.experience} onChange={handleGuardChange} />
//                   <label style={styles.label}>Rank *</label>
//                   <select name="rank" required value={guardForm.rank} onChange={handleGuardChange} style={styles.select}>
//                     <option value="">Select</option>
//                     {ranks.map((r, i) => (
//                       <option key={i}>{r}</option>
//                     ))}
//                     <option value="Other">Other</option>
//                   </select>
//                   {guardForm.rank === "Other" && (
//                     <FormInput label="Other Rank" required value={otherRank} onChange={(e) => setOtherRank(e.target.value)} />
//                   )}
//                 </>
//               )}

//               {guardStep === 3 && (
//                 <>
//                   <label style={styles.label}>Profile Picture *</label>
//                   <input type="file" accept="image/*" onChange={handleImageSelect} required style={styles.fileInput} />
//                   <FormInput label="Username" name="username" required value={guardForm.username} onChange={handleGuardChange} />
//                   <FormInput label="Password" name="password" type="password" required value={guardForm.password} onChange={handleGuardChange} />
//                 </>
//               )}

//               <div style={styles.navBtns}>
//                 {guardStep > 1 && (
//                   <button type="button" style={styles.backBtn} onClick={() => setGuardStep(guardStep - 1)}>
//                     Back
//                   </button>
//                 )}
//                 {guardStep < 3 ? (
//                   <button type="button" style={styles.nextBtn} onClick={() => setGuardStep(guardStep + 1)}>
//                     Next
//                   </button>
//                 ) : (
//                   <SubmitButton label={loading ? "Submitting..." : "Register Officer"} disabled={loading} />
//                 )}
//               </div>
//             </form>
//           </>
//         ) : (
//           <>
//             <StepHeader step={vipStep} />
//             <form onSubmit={handleVipSubmit}>
//               <div className="text-center mb-4">
//                 <h1 className="fw-bold display-6" style={{ color: "#ffffff" }}>
//                   VIP Registration Form
//                 </h1>
//                 <p style={{ color: "#e0e0e0" }}>Please fill in the details carefully</p>
//               </div>

//               {vipStep === 1 && (
//                 <>
//                   <FormInput label="Name" name="name" required value={vipForm.name} onChange={handleVipChange} />
//                   <FormInput label="Email" name="email" required value={vipForm.email} onChange={handleVipChange} />
//                   {emailError && <p style={styles.errorText}>{emailError}</p>}
//                   <FormInput label="Phone" name="contactno" required value={vipForm.contactno} onChange={handleVipChange} />
//                   {contactError && <p style={styles.errorText}>{contactError}</p>}
//                 </>
//               )}

//               {vipStep === 2 && (
//                 <>
//                   <label style={styles.label}>Gender *</label>
//                   <select name="gender" required value={vipForm.gender} onChange={handleVipChange} style={styles.select}>
//                     <option value="">Select Gender</option>
//                     <option>Male</option>
//                     <option>Female</option>
//                     <option>Other</option>
//                   </select>
//                   <FormInput label="Aadhaar Number" name="adharNo" required value={vipForm.adharNo} onChange={handleVipChange} />
//                   <label style={styles.label}>Profile Picture *</label>
//                   <input type="file" accept="image/*" onChange={handleVipImageSelect} required style={styles.fileInput} />
//                 </>
//               )}

//               {vipStep === 3 && (
//                 <>
//                   <label style={styles.label}>Designation *</label>
//                   <select name="designation" required value={vipForm.designation} onChange={handleVipChange} style={styles.select}>
//                     <option value="">Select</option>
//                     {designations.map((d, i) => (
//                       <option key={i} value={d.name || d}>
//                         {d.name || d}
//                       </option>
//                     ))}
//                     <option value="Other">Other</option>
//                   </select>
//                   {vipForm.designation === "Other" && (
//                     <FormInput
//                       label="Other Designation"
//                       required
//                       value={otherDesignation}
//                       onChange={(e) => setOtherDesignation(e.target.value)}
//                     />
//                   )}
//                   <FormInput label="Username" name="username" required value={vipForm.username} onChange={handleVipChange} />
//                   <FormInput label="Password" name="password" type="password" required value={vipForm.password} onChange={handleVipChange} />
//                 </>
//               )}

//               <div style={styles.navBtns}>
//                 {vipStep > 1 && (
//                   <button type="button" style={styles.backBtn} onClick={() => setVipStep(vipStep - 1)}>
//                     Back
//                   </button>
//                 )}
//                 {vipStep < 3 ? (
//                   <button type="button" style={styles.nextBtn} onClick={() => setVipStep(vipStep + 1)}>
//                     Next
//                   </button>
//                 ) : (
//                   <SubmitButton label={loading ? "Submitting..." : "Register VIP"} disabled={loading} />
//                 )}
//               </div>
//             </form>
//           </>
//         )}
//       </div>
//     </div>
//   );
// }

// /* ---------------- STYLES ---------------- */
// const styles = {
//   page: {
//     minHeight: "100vh",
//     backgroundImage: "url('/assets/img/loginPage.png')",
//     backgroundSize: "cover",
//     backgroundPosition: "center",
//     display: "flex",
//     flexDirection: "column",
//     alignItems: "center",
//     padding: "20px 20px 40px",
//   },
//   switchBox: {
//     display: "flex",
//     gap: 12,
//     marginBottom: 30,
//     flexWrap: "wrap",
//     justifyContent: "center",
//   },
//   switchBtn: (active) => ({
//     padding: "12px 28px",
//     borderRadius: 12,
//     border: "1px solid #3B82F6",
//     background: active ? "#3B82F6" : "rgba(255,255,255,0.1)",
//     color: active ? "#fff" : "#fff",
//     fontWeight: 600,
//     cursor: "pointer",
//     backdropFilter: "blur(4px)",
//     transition: "all 0.3s",
//   }),
//   card: {
//     width: "100%",
//     maxWidth: 520,
//    background: "rgba(249, 245, 245, 0.45)",
//     // backdropFilter: "blur(10px)",
//     // WebkitBackdropFilter: "blur(16px)",
//     border: "1px solid rgba(255, 255, 255, 0.15)",
//     padding: 32,
//     borderRadius: 20,
//     boxShadow: "0 20px 40px rgba(0, 0, 0, 0.4)",
//     color: "#ffffff",
//   },
//   steps: {
//     display: "flex",
//     justifyContent: "space-between",
//     marginBottom: 30,
//   },
//   stepWrapper: { textAlign: "center", flex: 1 },
//   stepCircle: {
//     width: 40,
//     height: 40,
//     borderRadius: "50%",
//     display: "flex",
//     alignItems: "center",
//     justifyContent: "center",
//     margin: "0 auto 10px",
//     fontWeight: 700,
//     color: "#fff",
//   },
//   stepText: { fontSize: 13, color: "#e0e0e0" },
//   label: {
//     fontWeight: 600,
//     marginBottom: 8,
//     display: "block",
//     color: "#e0e0e0",
//   },
//   select: {
//     width: "100%",
//     padding: 14,
//     borderRadius: 12,
//     border: "1px solid rgba(255, 255, 255, 0.2)",
//     color: "#100707ff",
//     background: "rgba(247, 236, 236, 0.1)",
//     marginBottom: 16,
//     fontSize: 16,
//   },
//   fileInput: {
//     width: "100%",
//     padding: "12px",
//     borderRadius: 12,
//     border: "1px solid rgba(255, 255, 255, 0.2)",
//     background: "rgba(255, 255, 255, 0.1)",
//     color: "#ffffff",
//     marginBottom: 16,
//   },
//   errorText: {
//     color: "#ff6b6b",
//     fontSize: 14,
//     marginTop: -8,
//     marginBottom: 12,
//   },
//   navBtns: {
//     display: "flex",
//     justifyContent: "space-between",
//     marginTop: 30,
//   },
//   nextBtn: {
//     background: "#3B82F6",
//     color: "#fff",
//     padding: "14px 32px",
//     borderRadius: 12,
//     border: "none",
//     cursor: "pointer",
//     fontWeight: 600,
//     fontSize: 16,
//     boxShadow: "0 4px 12px rgba(59, 130, 246, 0.4)",
//   },
//   backBtn: {
//     background: "rgba(255, 255, 255, 0.15)",
//     color: "#fff",
//     padding: "14px 32px",
//     borderRadius: 12,
//     border: "1px solid rgba(255, 255, 255, 0.3)",
//     cursor: "pointer",
//     fontWeight: 600,
//     fontSize: 16,
//   },
// };